import type { ApplyMatchSkip } from '../../services/integrationApi';

const T = 'integrations.catalogScan.skips';

type Translate = (key: string, values?: Record<string, unknown>) => string;

const EVIDENCE = ['sku', 'barcode', 'brand', 'title'] as const;

const KNOWN = new Set([
  'pro_only_excluded',
  'refurbished_excluded',
  'rental_excluded',
  'foreign_listing',
  'foreign_listing_unknown',
  'identity_mismatch',
  'identity_review',
  'shopify_not_found',
  'shopify_rate_limited',
  'shopify_sku_mismatch',
  'shopify_rejected',
  'shopify_archived',
  'shopify_update_failed',
  'link_failed',
]);

const UPDATE_CODES = new Set([
  'shopify_not_found',
  'shopify_rate_limited',
  'shopify_sku_mismatch',
  'shopify_rejected',
  'shopify_archived',
  'shopify_update_failed',
  'link_failed',
]);

/**
 * The sentence under one product. `update` is a row that linked and then the Shopify write failed,
 * so the wording says it is linked. Codes are translated; `detail` carries the server's short message.
 */
export function applyMatchSkipReason(
  skip: ApplyMatchSkip,
  t: Translate,
  phase: 'link' | 'update' = 'link',
): string {
  if (phase === 'update') {
    const code = UPDATE_CODES.has(skip.code) ? skip.code : 'other';
    return t(`${T}.updateReason.${code}`, { detail: skip.message });
  }
  const evidence = skip.evidence;
  if ((skip.code === 'identity_mismatch' || skip.code === 'identity_review') && evidence) {
    const values: Record<string, string> = {};
    for (const kind of EVIDENCE) {
      values[kind] = t(`${T}.evidence.${kind}.${evidence[kind]}`);
    }
    return t(`${T}.reason.${skip.code}`, values);
  }
  if (skip.code === 'foreign_listing') {
    return skip.listingSkus
      ? t(`${T}.reason.foreign_listing`, { skus: skip.listingSkus })
      : t(`${T}.reason.foreign_listing_unknown`);
  }
  if (KNOWN.has(skip.code)) {
    return t(`${T}.reason.${skip.code}`, { detail: skip.message });
  }
  return t(`${T}.reason.other`, { detail: skip.message });
}

/** Skips a Retry button can send again. A catalog gate or a wrong SKU is not one of them. */
export function retryableMatchSkips(skips: ApplyMatchSkip[]): ApplyMatchSkip[] {
  return skips.filter((skip) => skip.retryable === true);
}
